import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './VoyageTourisme.css';
import { 
  FaPlane, 
  FaMapMarkedAlt, 
  FaHotel, 
  FaUtensils, 
  FaLandmark, 
  FaArrowLeft,
  FaCheckCircle,
  FaCalendarAlt,
  FaEuroSign,
  FaTrain,
  FaBus,
  FaWalking,
  FaGlobeEurope
} from 'react-icons/fa';

const VoyageTourisme = () => {
  const navigate = useNavigate();

  const destinations = [
    {
      city: "Paris",
      region: "Île-de-France",
      description: "La Ville Lumière, ses monuments emblématiques, sa gastronomie et sa culture unique",
      image: "🗼",
      highlights: ["Tour Eiffel", "Louvre", "Notre-Dame", "Montmartre"]
    },
    {
      city: "Bordeaux",
      region: "Nouvelle-Aquitaine",
      description: "Capitale mondiale du vin, architecture classée UNESCO et art de vivre",
      image: "🍷",
      highlights: ["Cité du Vin", "Miroir d'eau", "Saint-Émilion", "Dunes du Pilat"]
    },
    {
      city: "Lyon",
      region: "Auvergne-Rhône-Alpes",
      description: "Capitale de la gastronomie, patrimoine Renaissance et vie culturelle",
      image: "🍽️",
      highlights: ["Vieux Lyon", "Traboules", "Musée des Confluences", "Bouchons lyonnais"]
    },
    {
      city: "Nice",
      region: "Provence-Alpes-Côte d'Azur",
      description: "La perle de la Côte d'Azur, entre mer et montagne",
      image: "🌊",
      highlights: ["Promenade des Anglais", "Vieux Nice", "Colline du Château", "Marché aux fleurs"]
    },
    {
      city: "Strasbourg",
      region: "Grand Est",
      description: "Capitale de Noël, mélange unique de cultures française et allemande",
      image: "🎄",
      highlights: ["Cathédrale", "Petite France", "Marché de Noël", "Parlement européen"]
    },
    {
      city: "Marseille",
      region: "Provence-Alpes-Côte d'Azur",
      description: "Ville multiculturelle, authenticité méditerranéenne et calanques",
      image: "⛵",
      highlights: ["Vieux-Port", "Calanques", "Notre-Dame de la Garde", "MuCEM"]
    }
  ];

  const activities = [
    {
      icon: <FaLandmark />,
      title: "Visites culturelles",
      description: "Musées, monuments historiques, sites classés UNESCO",
      price: "Gratuit - 20€",
      tips: "Profitez du premier dimanche du mois pour les musées gratuits"
    },
    {
      icon: <FaUtensils />,
      title: "Gastronomie",
      description: "Découverte des spécialités régionales, marchés locaux",
      price: "10€ - 50€",
      tips: "Le midi, les menus sont souvent moins chers qu'en soirée"
    },
    {
      icon: <FaWalking />,
      title: "Randonnées",
      description: "Sentiers balisés, parcs naturels, calanques",
      price: "Gratuit",
      tips: "Prévoyez de bonnes chaussures et de l'eau"
    },
    {
      icon: <FaHotel />,
      title: "Hébergements",
      description: "Auberges de jeunesse, Airbnb, hôtels, échanges de logements",
      price: "20€ - 100€/nuit",
      tips: "Réservez à l'avance pour les périodes de vacances"
    }
  ];

  const transportTips = [
    {
      icon: <FaTrain />,
      title: "SNCF et TER",
      tip: "Carte Avantage Jeune (-27 ans) : -30% sur les billets",
      price: "49€/an"
    },
    {
      icon: <FaBus />,
      title: "FlixBus & Blablacar Bus",
      tip: "Trajets low-cost entre grandes villes",
      price: "À partir de 5€"
    },
    {
      icon: <FaPlane />,
      title: "Vols low-cost",
      tip: "Ryanair, EasyJet, Volotea pour les destinations européennes",
      price: "À partir de 20€"
    },
    {
      icon: <FaGlobeEurope />,
      title: "Interrail",
      tip: "Pass pour voyager en Europe en train",
      price: "À partir de 200€"
    }
  ];

  const discounts = [
    "Carte Culture - Réductions dans les musées et monuments",
    "Carte Jeune SNCF -30% sur les trajets en train",
    "Pass Tourismes régionaux (Paris Museum Pass, etc.)",
    "Tarif étudiant dans de nombreux musées (souvent gratuit)",
    "Réductions avec la carte ISIC (International Student Identity Card)",
    "Offres spéciales sur Blablacar et FlixBus pour les étudiants"
  ];

  const upcomingEvents = [
    {
      name: "Fête de la Musique",
      date: "21 juin",
      location: "Partout en France",
      description: "Concerts gratuits dans les rues"
    },
    {
      name: "Nuit des Musées",
      date: "Mai",
      location: "Musées nationaux",
      description: "Entrée gratuite en soirée"
    },
    {
      name: "Festival d'Avignon",
      date: "Juillet",
      location: "Avignon",
      description: "Spectacles de théâtre et danse"
    },
    {
      name: "Marchés de Noël",
      date: "Décembre",
      location: "Strasbourg, Colmar, Paris",
      description: "Ambiance féerique et spécialités"
    }
  ];

  return (
    <div className="vt-page">
      {/* Hero Section */}
      <div className="vt-hero vt-hero--travel">
        <div className="vt-hero__background"></div>
        <div className="vt-hero__content">
          <button className="vt-back-button" onClick={() => navigate('/nos-services')}>
            <FaArrowLeft /> Retour aux services
          </button>
          <div className="vt-hero__icon">
            <FaPlane />
          </div>
          <h1 className="vt-hero__title">Voyage & Tourisme</h1>
          <p className="vt-hero__subtitle">Explorez la France et l'Europe : organisation de voyages et découvertes culturelles</p>
        </div>
      </div>

      <div className="vt-content">
        <div className="vt-container">
          {/* Introduction */}
          <div className="vt-intro">
            <h2 className="vt-section-title">Découvrez la France et l'Europe</h2>
            <p className="vt-intro__text">
              Étudier en France, c'est aussi l'opportunité unique de découvrir un patrimoine culturel exceptionnel 
              et de voyager à travers l'Europe. Que vous soyez amateur d'art, de gastronomie, de nature ou de 
              vie citadine, notre service vous accompagne dans l'organisation de vos voyages et vous fait bénéficier 
              des meilleures astuces pour voyager à petit prix.
            </p>
          </div>

          {/* Destinations populaires */}
          <div className="vt-section">
            <h2 className="vt-section-title">Destinations incontournables en France</h2>
            <div className="vt-grid vt-grid--destinations">
              {destinations.map((dest, index) => (
                <div className="vt-card vt-card--destination" key={index}>
                  <div className="vt-card__emoji">{dest.image}</div>
                  <div className="vt-card__info">
                    <h3 className="vt-card__title">{dest.city}</h3>
                    <p className="vt-card__region">{dest.region}</p>
                    <p className="vt-card__description">{dest.description}</p>
                    <div className="vt-card__highlights">
                      {dest.highlights.map((highlight, i) => (
                        <span key={i} className="vt-highlight-tag">{highlight}</span>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Activités */}
          <div className="vt-section">
            <h2 className="vt-section-title">Activités et expériences</h2>
            <div className="vt-grid vt-grid--activities">
              {activities.map((activity, index) => (
                <div className="vt-card vt-card--activity" key={index}>
                  <div className="vt-card__activity-icon">{activity.icon}</div>
                  <h3 className="vt-card__title">{activity.title}</h3>
                  <p className="vt-card__description">{activity.description}</p>
                  <div className="vt-card__price">
                    <FaEuroSign className="vt-card__price-icon" />
                    <span>{activity.price}</span>
                  </div>
                  <div className="vt-card__tip">
                    <FaCheckCircle className="vt-card__tip-icon" />
                    <span>{activity.tips}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Transport */}
          <div className="vt-section">
            <h2 className="vt-section-title">Astuces transport</h2>
            <div className="vt-grid vt-grid--transport">
              {transportTips.map((transport, index) => (
                <div className="vt-card vt-card--transport" key={index}>
                  <div className="vt-card__transport-icon">{transport.icon}</div>
                  <h3 className="vt-card__title">{transport.title}</h3>
                  <p className="vt-card__description">{transport.tip}</p>
                  <div className="vt-card__transport-price">À partir de {transport.price}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Réductions étudiants */}
          <div className="vt-section">
            <h2 className="vt-section-title">Réductions et bons plans étudiants</h2>
            <div className="vt-discounts-grid">
              {discounts.map((discount, index) => (
                <div className="vt-discount-item" key={index}>
                  <FaCheckCircle className="vt-discount-item__check" />
                  <span>{discount}</span>
                </div>
              ))}
            </div>
            <div className="vt-discount-note">
              <FaCalendarAlt className="vt-discount-note__icon" />
              <p>Pensez à toujours présenter votre carte étudiante pour bénéficier des réductions !</p>
            </div>
          </div>

          {/* Événements culturels */}
          <div className="vt-section">
            <h2 className="vt-section-title">Événements culturels à ne pas manquer</h2>
            <div className="vt-events-list">
              {upcomingEvents.map((event, index) => (
                <div className="vt-event-card" key={index}>
                  <div className="vt-event-card__date">{event.date}</div>
                  <div className="vt-event-card__info">
                    <h3 className="vt-event-card__name">{event.name}</h3>
                    <p className="vt-event-card__location">{event.location}</p>
                    <p className="vt-event-card__description">{event.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Conseils pratiques */}
          <div className="vt-section">
            <h2 className="vt-section-title">Conseils pratiques pour voyager</h2>
            <div className="vt-grid vt-grid--tips">
              <div className="vt-tip-item">
                <h3 className="vt-tip-item__title">📅 Anticiper</h3>
                <p className="vt-tip-item__text">Réservez vos billets de train et hébergements 2-3 mois à l'avance pour les meilleurs prix</p>
              </div>
              <div className="vt-tip-item">
                <h3 className="vt-tip-item__title">🎒 Voyager léger</h3>
                <p className="vt-tip-item__text">Privilégiez un sac à dos pour les week-ends, vous serez plus mobile</p>
              </div>
              <div className="vt-tip-item">
                <h3 className="vt-tip-item__title">📱 Applications utiles</h3>
                <p className="vt-tip-item__text">SNCF Connect, Citymapper, Google Maps, BlaBlaCar, Booking.com</p>
              </div>
              <div className="vt-tip-item">
                <h3 className="vt-tip-item__title">🆔 Documents</h3>
                <p className="vt-tip-item__text">Toujours avoir sur soi : carte d'identité/passeport, carte étudiante, justificatifs</p>
              </div>
            </div>
          </div>

          {/* Contact Section */}
          <div className="vt-contact">
            <h2 className="vt-section-title vt-section-title--centered">Organisez votre prochain voyage</h2>
            <p className="vt-contact__text">Nous vous aidons à planifier vos déplacements et à profiter des meilleures offres</p>
            <div className="vt-contact-buttons">
              <Link to="/contact" className="vt-button vt-button--primary">
                <FaPlane /> Réserver un voyage
              </Link>
              <Link to="/rendez-vous" className="vt-button vt-button--secondary">
                <FaMapMarkedAlt /> Demander un itinéraire
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VoyageTourisme;